import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { MusicVideoClip } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { clipNumber } from '../doc/clipOps';
import { isVerticalFootage } from '../footage/footageView';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { formatTime, sectionAt } from '../timeline/timelineGeometry';
import { ClipActions } from './ClipActions';
import { ClipControls } from './ClipControls';
import { ClipFitPicker } from './ClipFitPicker';
import { ClipSourceLine } from './ClipSourceLine';
import { ClipTrim } from './ClipTrim';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The inspector of one clip (design b1, c3, h3): its place on the song, its footage, the part of the
 * footage, the speed, the move, the cut-in, the fit of a vertical clip, and the clip actions.
 */
const ClipInspectorComponent: React.FC<{ clip: MusicVideoClip }> = ({ clip }) => {
  const intl = useIntl();
  const { doc, songMap, footage } = useMusicVideoEditor();
  const view = footage.lookup(clip.source);
  const section = useMemo(
    () => sectionAt(songMap.sections, (clip.startSec + clip.endSec) / 2),
    [clip.endSec, clip.startSec, songMap.sections]
  );
  const beats = useMemo(
    () => songMap.beats.filter((sec) => sec >= clip.startSec - 0.001 && sec < clip.endSec - 0.001).length,
    [clip.endSec, clip.startSec, songMap.beats]
  );
  const from = formatTime(clip.startSec, true);
  const to = formatTime(clip.endSec, true);
  const isVertical = view !== null && isVerticalFootage(view);

  return (
    <>
      <h4 className={styles.inspectorHead}>
        {intl.formatMessage(panelMessages.clipHeading, { number: clipNumber(doc, clip.id) ?? 0 })}
        <span className={styles.inspectorSub}>
          {section === null
            ? intl.formatMessage(panelMessages.clipSubNoSection, { from, to })
            : intl.formatMessage(panelMessages.clipSub, { section: section.label, from, to, beats })}
        </span>
      </h4>
      <div className={styles.clipFacts}>
        <span
          className={clsx(styles.clipThumb, isVertical && styles.clipThumbTall)}
          style={view === null || view.posterUrl === null ? undefined : { backgroundImage: `url("${view.posterUrl}")` }}
        />
        <div className={styles.column}>
          <b>{view?.label ?? ''}</b>
          <ClipSourceLine view={view} />
        </div>
      </div>
      {view === null && <p className={styles.hint}>{intl.formatMessage(panelMessages.unknownClip)}</p>}
      {view?.origin === 'ai' && view.prompt !== null && <p className={styles.prompt}>{view.prompt}</p>}
      <ClipTrim clip={clip} view={view} />
      {isVertical && view !== null && <ClipFitPicker clip={clip} view={view} />}
      <ClipControls clip={clip} />
      <div className={styles.separator} />
      <ClipActions clip={clip} view={view} section={section} />
      <span className={styles.grow} />
      <p className={styles.hint}>
        {intl.formatMessage(
          view?.origin === 'ai'
            ? panelMessages.aiClipHint
            : clip.source.kind === 'footage'
              ? panelMessages.ownKeepHint
              : panelMessages.keepHint
        )}
      </p>
    </>
  );
};

export const ClipInspector = TypedMemo(ClipInspectorComponent);
